/**
 * <Faq /> — the /faq page, and the FAQPage JSON-LD that goes with it.
 *
 * ONE SOURCE, TWO OUTPUTS
 *   The visible page and the structured data are both built from
 *   src/content/faq.js, in the same render. Google treats FAQ structured
 *   data that does not match the visible text as a policy violation, and
 *   the usual cause is two copies of the words drifting apart. There is
 *   only one copy here, so they cannot drift. Never paste an answer into
 *   buildFaqJsonLd().
 *
 * WHY THE HEADINGS ARE SHAPED THIS WAY
 *   <h1> comes from <Page>. Each group is a <section> with an <h2>, and
 *   each question is an <h3> above its answer. That gives a reader using a
 *   screen reader, and a crawler reading the outline, the same two-level
 *   structure the JSON-LD describes.
 *
 * LINKABLE ANSWERS
 *   Every question carries its id, so /faq#own-vendors opens at that
 *   question. A support reply can point at one answer instead of a page.
 *
 * See docs/aeo/AEO-LEDGER.md, task 10.
 */

import styled from 'styled-components';

import Page from '../Page';
import Seo from '../Seo';
import Footer from '../Shared/NewFooter';
import Header from '../Shared/NewHeader';
import faq, { allQuestions } from '../../content/faq';

import { colors, ContentWrapper, mobile, PageWrapper, tablet } from '../styles';

/**
 * The FAQPage graph, built from the same data the page renders.
 *
 * Exported so scripts/check-faq.js can compare it with the rendered text
 * without reaching into the component.
 */
export const buildFaqJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  name: faq.title,
  mainEntity: allQuestions().map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer
    }
  }))
});

const Wrapper = styled(ContentWrapper)`
  & > main {
    max-width: 908px;
    margin: 0 auto;
    padding: 40px 44px 60px;

    @media (max-width: ${tablet}) {
      padding: 32px 24px 48px;
    }

    @media (max-width: ${mobile}) {
      padding: 24px 15px 40px;
    }
  }
`;

const Article = styled.div`
  color: ${colors.defaultText};
  font-size: 16px;
  line-height: 1.65;

  @media (max-width: ${mobile}) {
    font-size: 15px;
  }

  // These four rules are copied from src/components/Legal, deliberately, so
  // a content page reads the same whichever one a visitor lands on. The type
  // scale is 34 / 20 / 16 and nothing else. A question is body size and bold
  // rather than a fifth size, because a size the legal pages do not use is a
  // size that will drift away from them.
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 36px 0 12px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 26px 0 8px;
    // Clears the fixed header when /faq#own-vendors opens at a question.
    scroll-margin-top: 130px;
  }

  p {
    margin: 0 0 14px;
  }
`;

const Intro = styled.p`
  color: ${colors.fadedText};
  margin: 0 0 8px !important;
`;

const Faq = () => (
  <PageWrapper>
    <Seo jsonLd={buildFaqJsonLd()} />
    <Header omitSearch />
    <Wrapper>
      <Page title={faq.title}>
        <Article>
          <Intro>{faq.intro}</Intro>

          {faq.groups.map((group) => (
            <section key={group.id} aria-labelledby={group.id}>
              <h2 id={group.id}>{group.heading}</h2>
              {group.questions.map((item) => (
                <div key={item.id}>
                  <h3 id={item.id}>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          ))}
        </Article>
      </Page>
    </Wrapper>
    <Footer />
  </PageWrapper>
);

export default Faq;
